"use client";

import { Calendar, Compass, Heart, Palette, Sparkles, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const milestones = [{
  number: "01",
  timing: "PREMIERS JOURS",
  title: "Premier contact",
  description: "Un échange bienveillant pour comprendre vos besoins, votre rythme et vos attentes pour ce nouveau chapitre de vie.",
  icon: Heart
}, {
  number: "02",
  timing: "ORGANISATION",
  title: "Plan de soins",
  description: "Nous définissons ensemble un accompagnement sur-mesure, alliant repos, nutrition et soutien émotionnel.",
  icon: Palette
}, {
  number: "03",
  timing: "PRÉPARATION",
  title: "Cuisine & Nutrition",
  description: "Organisation de vos repas sains et nourrissants, pensés pour favoriser votre récupération postnatale.",
  icon: Users
}, {
  number: "04",
  timing: "ACCOMPAGNEMENT",
  title: "Soutien à domicile",
  description: "Présence douce et rassurante pour vous guider dans vos premiers pas de maman, en toute sérénité.",
  icon: Compass
}, {
  number: "05",
  timing: "SUIVI",
  title: "Ajustements",
  description: "Un suivi régulier pour adapter nos interventions à l'évolution de vos besoins et de ceux de votre bébé.",
  icon: Calendar
}, {
  number: "06",
  timing: "AUTONOMIE",
  title: "Vers l'équilibre",
  description: "Nous vous accompagnons vers une confiance retrouvée, pour savourer pleinement votre nouvelle vie de famille.",
  icon: Sparkles
}];
export default function ProgramTimeline() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const sectionRef = useRef<HTMLElement | null>(null);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const [fillProgress, setFillProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const el = timelineRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const total = rect.height + viewportH;
      const scrolled = viewportH - rect.top;
      const progress = Math.max(0, Math.min(1, scrolled / total));
      setFillProgress(progress);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);
  return <section data-section-id="429" id="programtimeline" ref={sectionRef} className="bg-[var(--background)] text-[var(--foreground)] relative overflow-hidden py-24 flex flex-col px-6 max-w-7xl mx-auto h-auto md:!h-[625.833px] md:py-40 md:px-10">
      <svg className="pointer-events-none absolute top-0 left-0 w-40 md:w-64 h-40 md:h-64 text-[var(--primary)]/20" viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="0.5" aria-hidden="true">
        <path d="M10,10 Q40,30 30,70 Q20,110 50,140 Q80,170 70,200" />
        <path d="M30,70 Q50,60 70,75" />
        <path d="M50,140 Q70,130 90,145" />
        <ellipse cx="35" cy="65" rx="8" ry="3" transform="rotate(-30 35 65)" />
        <ellipse cx="55" cy="135" rx="8" ry="3" transform="rotate(-30 55 135)" />
        <ellipse cx="75" cy="80" rx="6" ry="2.5" transform="rotate(20 75 80)" />
      </svg>
      <svg className="pointer-events-none absolute bottom-0 right-0 w-40 md:w-64 h-40 md:h-64 text-[var(--primary)]/20 rotate-180" viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="0.5" aria-hidden="true">
        <path d="M10,10 Q40,30 30,70 Q20,110 50,140 Q80,170 70,200" />
        <path d="M30,70 Q50,60 70,75" />
        <path d="M50,140 Q70,130 90,145" />
        <ellipse cx="35" cy="65" rx="8" ry="3" transform="rotate(-30 35 65)" />
        <ellipse cx="55" cy="135" rx="8" ry="3" transform="rotate(-30 55 135)" />
      </svg>

      <div className="relative w-full">
        <div ref={headerRef} className={`flex flex-col md:flex-row md:items-start justify-between gap-6 md:gap-12 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="max-w-2xl">
            <div className="flex items-center gap-4 mb-8">
              <span className="h-px w-12 bg-[var(--primary)]/60" />
              <span className="font-sans text-xs tracking-[0.3em] text-[var(--primary)] uppercase">LE CERCLE&nbsp;</span>
            </div>
            <h1 className="font-serif font-default text-3xl md:text-4xl lg:text-5xl leading-[1.05] text-[var(--foreground)]">Cercle<span className="font-serif text-[var(--primary)]"><br />maman &amp; bébé<br />0 à 4 mois</span></h1>
            <p className="mt-8 font-sans text-lg md:text-xl text-[var(--foreground)]/80 max-w-xl leading-relaxed">Six semaines pour rencontrer d'autres mamans, être accompagnée par des expertes et vivre ton post-partum entourée.</p>
          </div>
          <div className="relative w-full md:w-1/2 aspect-[3/4] md:aspect-square lg:aspect-[4/3] shrink-0">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/mother_baby_circle.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/mother_baby_circle.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/mother_baby_circle.webp-wvc-sizes" alt="Maman et bébé participant à un cercle de soutien bienveillant" className="w-full h-full object-cover rounded-2xl rounded-br-[120px] shadow-lg" loading="lazy" decoding="async" src="https://quality-chicken.10web.cloud/wp-content/uploads/2026/06/Untitled.png" />
          </div>
        </div>

        <div ref={timelineRef} className="relative mt-12 md:mt-24">
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px md:-translate-x-1/2 bg-[var(--primary)]/20" aria-hidden="true" />
          <div className="absolute left-6 md:left-1/2 top-0 w-px md:-translate-x-1/2 bg-gradient-to-b from-[var(--primary)] via-[var(--primary)] to-[var(--primary)]/40 transition-all duration-300 ease-out" style={{
          height: `${fillProgress * 100}%`
        }} aria-hidden="true" />

          
        </div>

        
      </div>
    </section>;
}
function MilestoneItem({
  number,
  timing,
  title,
  description,
  Icon,
  isLeft,
  ...rest
}: {
  number: string;
  timing: string;
  title: string;
  description: string;
  Icon: React.ComponentType<{
    className?: string;
  }>;
  isLeft: boolean;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <div ref={ref} className="relative grid grid-cols-[3rem_1fr] md:grid-cols-2 md:gap-16 items-center" {...rest}>
      <div className="absolute left-6 md:left-1/2 -translate-x-1/2 z-10">
        <div className={`relative size-4 rounded-lg bg-[var(--primary)] transition-all duration-700 ${isVisible ? "scale-100 opacity-100" : "scale-50 opacity-40"}`}>
          <div className={`absolute inset-0 rounded-lg bg-[var(--primary)] blur-md transition-opacity duration-700 ${isVisible ? "opacity-70" : "opacity-0"}`} aria-hidden="true" />
          <div className={`absolute -inset-2 rounded-lg bg-[var(--primary)]/30 blur-xl transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`} aria-hidden="true" />
        </div>
      </div>

      <div className={`hidden md:block ${isLeft ? "" : "md:col-start-2 md:order-2"}`}>
        {isLeft && <ContentBlock number={number} timing={timing} title={title} description={description} Icon={Icon} isVisible={isVisible} alignRight />}
      </div>

      <div className={`hidden md:block ${isLeft ? "md:col-start-2" : "md:col-start-1 md:row-start-1"}`}>
        {!isLeft && <ContentBlock number={number} timing={timing} title={title} description={description} Icon={Icon} isVisible={isVisible} />}
      </div>

      <div className="md:hidden pl-6">
        <ContentBlock number={number} timing={timing} title={title} description={description} Icon={Icon} isVisible={isVisible} />
      </div>
    </div>;
}
function ContentBlock({
  number,
  timing,
  title,
  description,
  Icon,
  isVisible,
  alignRight = false
}: {
  number: string;
  timing: string;
  title: string;
  description: string;
  Icon: React.ComponentType<{
    className?: string;
  }>;
  isVisible: boolean;
  alignRight?: boolean;
}) {
  return <div className={`flex flex-col gap-4 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${alignRight ? "md:text-right" : "md:text-left"}`}>
      <div className={`flex items-center gap-3 ${alignRight ? "md:justify-end" : "md:justify-start"}`}>
        <Icon className="size-4 text-[var(--primary)]" />
        <span className="font-sans text-xs tracking-[0.25em] text-[var(--primary)] uppercase">
          {timing}
        </span>
      </div>
      <div className={`flex items-baseline gap-4 ${alignRight ? "md:justify-end" : "md:justify-start"}`}>
        <span className="font-serif font-default text-5xl md:text-6xl text-[var(--primary)]/80 font-light leading-none">
          {number}
        </span>
        <span className="h-px w-8 bg-[var(--primary)]/40" />
      </div>
      <h3 className="font-serif font-default text-3xl md:text-4xl text-[var(--foreground)] leading-tight">
        {title}
      </h3>
      <p className="font-sans text-base md:text-lg text-[var(--foreground)]/80 leading-relaxed max-w-md">
        {description}
      </p>
    </div>;
}